'use client'

import type { CmsFestivesTeaser } from '@/utils/getGlobalChrome'

import { useEffect, useState } from 'react'

export type FestivesTeaser = {
  showTeaser: boolean
  desktopImage: string
  mobileImage: string
}

/**
 * The festive teaser banner: takes its CMS entry (seeded at build time by
 * layout.tsx -> AppShell, see src/utils/getGlobalChrome.ts) and exposes whether
 * the current time falls inside its start/expiry window.
 *
 * The entry used to be fetched here on mount — one CMS round-trip on every page
 * load across all ~110 routes. A Strapi publish rebuilds and redeploys the site,
 * so there is nothing to revalidate. See tests/no-client-cms-fetch.spec.ts.
 *
 * `new Date()` stays inside the effect/interval — never in render — because a
 * build-time timestamp baked into the static HTML would mismatch on hydration.
 */
export function useFestivesTeaser(initialData?: CmsFestivesTeaser | null): FestivesTeaser {
  const [showTeaser, setShowTeaser] = useState(false)
  const desktopImage = initialData?.imageDesktopURL ?? ''
  const mobileImage = initialData?.imageMobileURL ?? ''
  const startDate = initialData?.startDate ? new Date(initialData.startDate) : null
  const expiryDate = initialData?.endDate ? new Date(initialData.endDate) : null

  useEffect(() => {
    if (startDate && expiryDate) {
      const checkDateRange = () => {
        const currentDate = new Date()
        // Check if the current date is between the start and expiry dates
        if (currentDate >= startDate && currentDate <= expiryDate) {
          setShowTeaser(true)
        } else {
          setShowTeaser(false)
        }
      }

      // Initial check
      checkDateRange()

      // Set an interval to check the date every second
      const intervalId = setInterval(checkDateRange, 1000)

      // Cleanup interval when component unmounts
      return () => clearInterval(intervalId)
    }
    // Keyed on the raw ISO strings: `startDate`/`expiryDate` are new Date objects
    // on every render, so object identity would re-run this effect endlessly.
  }, [initialData?.startDate, initialData?.endDate])

  return { showTeaser, desktopImage, mobileImage }
}
